import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Customize_Css/Pagination.css';
import { Dropdown } from 'react-bootstrap';

const OrderDataTable = () => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const AuthorizationToken = localStorage.getItem('token');
    const epicxTenantId = localStorage.getItem('epicxTenantId');
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;
    const [sortConfig, setSortConfig] = useState({ key: 'orderDate', direction: 'desc' });
    const [selectedMonth, setSelectedMonth] = useState(null);

    const fetchWithAuth = (url) => {
        if (!AuthorizationToken) {
            console.error('No authorization token found');
            return Promise.reject('No authorization token');
        }
        return fetch(url, {
            headers: {
                'Authorization': `Bearer ${AuthorizationToken}`,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            if (response.status === 401) {
                throw new Error('Unauthorized');
            }
            return response;
        });
    };

useEffect(() => {
    if (!epicxTenantId) {
        console.error('No epicxTenantId found');
        setData([]);
        return;
    }

    const url =
        selectedMonth !== null
            ? `${BASE_URL}/orders/api/orders/${epicxTenantId}/orders/by-month?month=${selectedMonth + 1}`
            : `${BASE_URL}/orders/api/orders/${epicxTenantId}`;

    fetchWithAuth(url)
        .then((response) => response.json())
        .then((responseData) => {
            let orders = [];
            if (responseData.orders && Array.isArray(responseData.orders)) {
                orders = responseData.orders;
            }
            else if (responseData.data && Array.isArray(responseData.data)) {
                orders = responseData.data;
            }
            else if (Array.isArray(responseData)) {
                orders = responseData;
            } else {
                console.warn('No valid order data:', responseData);
                setData([]);
                return;
            }

            const formattedData = orders.flatMap((order) =>
                order.products.map((item) => ({
                    orderId: order.orderId,
                    productName: item.productName,
                    productImage: item.productImages,
                    userName: order.address?.userName || 'N/A',
                    productWeight: item.productWeight,
                    perItemPrice: item.perItemPrice,
                    productQuantity: item.productQuantity,
                    productVendorName: order.productVendorName,
                    productStatus: order.orderStatus,
                    orderDate: new Date(order.orderDate).toLocaleString(),
                    orderDateRaw: new Date(order.orderDate).getTime(),
                    totalPrice: item.perItemPrice * item.productQuantity,
                }))
            );

            formattedData.sort((a, b) => b.orderDateRaw - a.orderDateRaw);
            setData(formattedData);
            setCurrentPage(1); 
        })
        .catch((error) => {
            console.error('Error fetching order data:', error);
            setData([]);
        });
}, [AuthorizationToken, selectedMonth, epicxTenantId]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setData((prevData) => {
            return [...prevData].sort((a, b) => {
                if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
                if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        });
    };

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(data.length / rowsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const getStatusStyles = (status) => {
        switch (String(status || '').toLowerCase()) {
            case 'order-placed':
                return { backgroundColor: '#fef3c7', color: '#92400e' };
            case 'order-confirmed':
                return { backgroundColor: '#dcfce7', color: '#166534' };
            case 'order-cancelled':
            case 'cancelled':
                return { backgroundColor: '#ffe4e6', color: '#be123c' };
            case 'order-completed':
            case 'order-delivered':
                return { backgroundColor: '#e0f2fe', color: '#075985' };
            case 'pending':
                return { backgroundColor: '#f1f5f9', color: '#475569' };
            default:
                return { backgroundColor: '#e7f8f4', color: '#0f766e' };
        }
    };

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const columns = [
        { key: 'orderId', label: 'Order Id' },
        { key: 'productName', label: 'Product' },
        { key: 'userName', label: 'Customer' },
        { key: 'productWeight', label: 'Weight' },
        { key: 'perItemPrice', label: 'Price' },
        { key: 'productQuantity', label: 'Qty' },
        { key: 'productVendorName', label: 'Vendor' },
        { key: 'productStatus', label: 'Status' },
        { key: 'orderDate', label: 'Order Date' },
        { key: 'totalPrice', label: 'Total' },
    ];

    const rangeStart = data.length ? indexOfFirstRow + 1 : 0;
    const rangeEnd = Math.min(indexOfLastRow, data.length);

    return (
        <section className="krv-order-panel">
            <div className="krv-ot-head">
                <div>
                    <h3>Order Details</h3>
                    <p>Products, customers, and totals for the selected month.</p>
                </div>
                <Dropdown>
                    <Dropdown.Toggle className="krv-ot-filter">
                        {selectedMonth !== null ? months[selectedMonth] : 'All Months'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="krv-ot-menu" align="end">
                        <Dropdown.Item onClick={() => setSelectedMonth(null)}>All Months</Dropdown.Item>
                        {months.map((month, index) => (
                            <Dropdown.Item
                                key={month}
                                active={selectedMonth === index}
                                onClick={() => setSelectedMonth(index)}
                            >
                                {month}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <div className="krv-ot-scroll">
                <table className="krv-ot-table">
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    onClick={() => handleSort(column.key)}
                                    aria-sort={
                                        sortConfig.key === column.key
                                            ? sortConfig.direction === 'asc' ? 'ascending' : 'descending'
                                            : 'none'
                                    }
                                >
                                    <span>{column.label}</span>
                                    <span className={`krv-ot-sort ${sortConfig.key === column.key ? 'is-active' : ''}`}>
                                        {sortConfig.key === column.key && sortConfig.direction === 'desc' ? '▼' : '▲'}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.length > 0 ? (
                            currentRows.map((item, index) => {
                                const [datePart, timePart] = String(item.orderDate || '').split(', ');
                                const statusLabel = String(item.productStatus || '')
                                    .replace(/order-/g, '')
                                    .replace(/-/g, ' ');
                                return (
                                    <tr key={`${item.orderId}-${index}`}>
                                        <td className="krv-ot-id" title={item.orderId}>{item.orderId}</td>
                                        <td>
                                            <div className="krv-ot-product">
                                                <img
                                                    src={item.productImage}
                                                    alt=""
                                                    className="krv-ot-thumb"
                                                    onError={(event) => {
                                                        event.currentTarget.style.display = 'none';
                                                    }}
                                                />
                                                <span>{item.productName}</span>
                                            </div>
                                        </td>
                                        <td>{item.userName}</td>
                                        <td>{item.productWeight} kg</td>
                                        <td>₹{item.perItemPrice}</td>
                                        <td>{item.productQuantity}</td>
                                        <td>{item.productVendorName}</td>
                                        <td>
                                            <span className="krv-ot-status" style={getStatusStyles(item.productStatus)}>
                                                {statusLabel}
                                            </span>
                                        </td>
                                        <td className="krv-ot-date">
                                            <span>{datePart}</span>
                                            {timePart ? <small>{timePart}</small> : null}
                                        </td>
                                        <td className="krv-ot-total">₹{item.totalPrice}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td className="krv-ot-empty" colSpan={columns.length}>
                                    {selectedMonth !== null
                                        ? `No orders found for ${months[selectedMonth]}.`
                                        : 'No orders found in the database.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {data.length > 0 && (
                <div className="krv-ot-footer">
                    <p>Showing {rangeStart}–{rangeEnd} of {data.length}</p>
                    <div className="krv-ot-pager">
                        <button type="button" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
                        <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                        <button type="button" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default OrderDataTable;